import { Injectable } from '@angular/core';
import {
  CheckinFunctionIdType,
  CheckinPageIdType,
  NEXT_ACTION,
  ParameterFromServicing,
  ParameterToServicing,
  RESERVATION_DETAIL_IDENTIFIER,
  SITE_ID,
} from '../../interface';
import { CheckinCommonState } from '../../store';
import { CheckinCommonStoreService } from '../checkin-common-store/checkin-common-store.service';
import { PostJourneysGetJourneyRequest, PostJourneysGetJourneyResponseDataTravelersInner } from 'src/sdk_checkin';
import { SessionStorageName } from '@lib/interfaces';
import { AswMasterService, CommonLibService } from '@lib/services';
import { CheckinBaseClass } from '../../components';
import { LOAD_MASTERS } from '../../master/master';
import { JourneyElements } from '../../interface/journey-elements';
import { PREVIOUS_PAGE_IDENTIFIER } from '@conf/previous-page-identifier.config';
import { ServicingMybookingTransitionService } from '../servicing-mybooking-transition/servicing-mybooking-transition.service';

@Injectable()
export class CheckinMybookingService extends CheckinBaseClass {
  /** サービシングから受け取るパラメータ */
  private _parameterFromServicing: ParameterFromServicing | undefined;
  /** チェックイン共通から受け取るパラメータ */
  private _parameterFromCheckinCommon: CheckinCommonState | undefined;

  /** APIから返却されたエラーコードをエラーメッセージIDに変換する */
  private readonly API_ERROR_CODE_TO_MESSAGE_KEY: { [errorCode: string]: string } = {
    EBAZ000349: 'E0597',
    EBAZ000350: 'E0598',
    EBAZ000351: 'E0599',
    EBAZ000776: 'E1782',
  };

  /** APIから返却されたワーニングコードをワーニングメッセージIDに変換する */
  private readonly API_WARNING_CODE_TO_MESSAGE_KEY: { [warningCode: string]: string } = {
    WBAZ000247: 'W0796',
  };

  constructor(
    private common: CommonLibService,
    private checkinCommonStoreService: CheckinCommonStoreService,
    private masterService: AswMasterService,
    private servicingMybookingTransitionService: ServicingMybookingTransitionService
  ) {
    super();
    // サービシングから遷移した際パラメータの取得
    this._parameterFromServicing = this.servicingMybookingTransitionService.getParameterFromServicing();
    // チェックイン系から遷移した際のパラメータの取得
    this._parameterFromCheckinCommon = this.getParameterFromCheckinCommon();

    // サービシングからの遷移かどうかを設定する
    if (this._parameterFromServicing?.SITE_ID === SITE_ID.RESERVATION) {
      // サービシングから受け取ったパラメータをチェックイン系の他の画面でも使用できるようにチェックイン共通に格納
      this.checkinCommonStoreService.updateCheckinCommon({
        journeyId: this._parameterFromServicing.journeyID,
        selectedFlightIds: this._parameterFromServicing.flightIds,
        reservationDetailIdentifier: RESERVATION_DETAIL_IDENTIFIER,
      });
    } else {
      this.checkinCommonStoreService.updateCheckinCommon({
        reservationDetailIdentifier: '',
      });
    }
  }

  get parameterFromServicing() {
    return this._parameterFromServicing;
  }

  get parameterFromCheckinCommon() {
    return this._parameterFromCheckinCommon;
  }

  /**
   * 画面遷移元からパラメータを受け取り、CPR情報取得APIのリクエストパラメータに成形して返却する
   * @returns CPR情報取得APIのリクエストパラメータ
   */
  getPostJourneysGetJourneyRequestParameter(): PostJourneysGetJourneyRequest | undefined {
    // サービシングからのパラメータを取得
    if (this._parameterFromServicing?.SITE_ID) {
      const journeyId = this._parameterFromServicing.journeyID;
      if (journeyId) {
        return {
          journeyId: journeyId,
          searchProperty: {
            isMaskingRequired: true,
            isUnFlightsReturnRequired: true,
          },
        };
      }
      // サービシングから受け取るパラメータから有効なパラメータが取得できない場合
      return undefined;
    }

    // サービシングからの遷移でない場合、チェックイン共通からパラメータの取得
    if (this._parameterFromCheckinCommon) {
      const journeyId = this._parameterFromCheckinCommon.journeyId;
      if (journeyId) {
        return {
          journeyId: journeyId,
          searchProperty: {
            isMaskingRequired: true,
            isUnFlightsReturnRequired: true,
          },
        };
      }
      // チェックイン共通から有効なパラメータが取得できない場合
      return undefined;
    }

    // どこのパラメータもundefinedの場合
    return undefined;
  }

  /**
   * チェックイン共通から必要なパラメータを取得する
   * @returns チェックイン共通に保存されているパラメータ
   */
  private getParameterFromCheckinCommon(): CheckinCommonState {
    // チェックイン検索、チェックイン対象選択で設定されてそれ以降変更はないので同期的に取得できる
    return this.checkinCommonStoreService.checkinCommon;
  }

  /**
   * 予約詳細画面へ遷移する
   * @param travelers 予約詳細画面へ渡す搭乗者情報リストを作成するための搭乗者配列
   */
  transitionServicingMybooking(travelers: PostJourneysGetJourneyResponseDataTravelersInner[]) {
    if (!this._parameterFromServicing) {
      return;
    }

    // 遷移先に渡すパラメータの設定
    const param: ParameterToServicing = {
      JSessionId: sessionStorage.getItem(SessionStorageName.JSESSION_ID),
      searchType: 'order',
      orderId: this._parameterFromServicing.orderId,
      firstName: this._parameterFromServicing.firstName,
      lastName: this._parameterFromServicing.lastName,
      SITE_ID: SITE_ID.ASW_APP,
      travelers: this.servicingMybookingTransitionService.concatTravelerNames(travelers),
    };

    this.servicingMybookingTransitionService.transitionToServicing(param);
  }

  /**
   * 戻るボタン押下時に予約詳細に遷移する
   * */
  backToServicingMybooking() {
    if (!this._parameterFromServicing) {
      return;
    }

    // 遷移先に渡すパラメータの設定
    const param: ParameterToServicing = {
      searchType: 'order',
      orderId: this._parameterFromServicing.orderId,
      firstName: this._parameterFromServicing.firstName,
      lastName: this._parameterFromServicing.lastName,
      SITE_ID: SITE_ID.ASW_APP,
    };

    this.servicingMybookingTransitionService.transitionToServicing(param);
  }

  /**
   * 予約詳細（サービス）に遷移する
   * @param travelers 予約詳細画面へ渡す搭乗者情報リストを作成するための搭乗者配列
   * @param flights 予約詳細画面へ渡すorderIdを取得するためのセグメントID
   * @param journeyElements 予約詳細画面へ渡すorderIdを取得するためのjourneyElementオブジェクト
   */
  transitionServicingMybookingService(
    travelers: PostJourneysGetJourneyResponseDataTravelersInner[],
    flightId: string,
    journeyElements: JourneyElements
  ) {
    // どこからの遷移かによって取得元が変更されるパラメータ
    let orderId: string | undefined;
    let firstName: string | undefined;
    let lastName: string | undefined;
    let journeyId: string | undefined;
    let flightIdList: string[] | undefined;

    // 予約詳細から受け取ったパラメータが存在する場合
    if (this._parameterFromServicing) {
      journeyId = this._parameterFromServicing?.journeyID;
      flightIdList = this._parameterFromServicing?.flightIds;
      orderId = this._parameterFromServicing?.orderId;
      firstName = this._parameterFromServicing?.firstName;
      lastName = this._parameterFromServicing?.lastName;
    }
    // チェックインフローからの遷移の場合
    else {
      journeyId = this._parameterFromCheckinCommon?.journeyId;
      flightIdList = this._parameterFromCheckinCommon?.selectedFlightIds;
      const travelerId = travelers[0].id;
      orderId = journeyElements[flightId][travelerId].orderId;
      firstName = this.common.aswServiceStoreService.aswServiceData.firstName;
      lastName = this.common.aswServiceStoreService.aswServiceData.lastName;
    }

    // 遷移先に渡すパラメータの設定
    const param: ParameterToServicing = {
      searchType: 'order',
      orderId: orderId,
      firstName: firstName,
      lastName: lastName,
      nextAction: NEXT_ACTION.SERVICE,
      SITE_ID: SITE_ID.ASW_APP,
      JSessionId: sessionStorage.getItem(SessionStorageName.JSESSION_ID),
      JourneyId: journeyId,
      flightIdList: flightIdList,
      travelers: this.servicingMybookingTransitionService.concatTravelerNames(travelers),
      previousScreenId: `${CheckinFunctionIdType.BOARDING_PROCEDURES}-${CheckinPageIdType.CHECKIN_MYBOOKING}`,
    };

    this.servicingMybookingTransitionService.transitionToServicing(param);
  }

  /**
   * エラーコードを受け取り、エラーメッセージIDを返却する
   * @param apiErrorCode
   * @returns 指定されたエラーコードに紐づくエラーメッセージID、存在しない場合はundefined
   */
  getErrorMessageKey(apiErrorCode: string): string | undefined {
    return this.API_ERROR_CODE_TO_MESSAGE_KEY[apiErrorCode];
  }

  /**
   * ワーニングコードを受け取り、ワーニングメッセージIDを返却する
   * @param apiErrorCode
   * @returns 指定されたワーニングコードに紐づくワーニングメッセージID、存在しない場合はundefined
   */
  getWarningMessageKey(apiWarningCode: string): string | undefined {
    return this.API_WARNING_CODE_TO_MESSAGE_KEY[apiWarningCode];
  }

  /** チェックイン共通Storeにチェックイン状況詳細からの遷移であることを格納する */
  setPreviousIdentifier() {
    this.checkinCommonStoreService.updateCheckinCommon({
      previousPageIdentifier: PREVIOUS_PAGE_IDENTIFIER.CHECKIN_STATUS,
    });
  }
  /**
   * チェックイン状況詳細画面で使用するマスターをロードするObservableを取得する
   * 既にロードされているマスターを上書きしないために、更新フラグをtrueで呼び出す
   */
  getLoadMasters$() {
    return this.masterService.load(
      [
        LOAD_MASTERS.AIRLINE_I18NJOIN_BY_AIRLINE_CODE,
        LOAD_MASTERS.AIRPORT_I18NJOIN_BY_AIRPORT_CODE,
        LOAD_MASTERS.EQUIPMENT_BY_ACV,
        LOAD_MASTERS.COUNTRY_I18NJOIN_BY_COUNTRY_2_LETTER_CODE,
        LOAD_MASTERS.MILEAGE_PROGRAM_BY_CARRIER_CODE,
        LOAD_MASTERS.STATE_BY_STATE_CODE,
        LOAD_MASTERS.V_LIST_DATA_CABIN_CLASS_BY_SEARCH_KEY,
        LOAD_MASTERS.FUNCTION_INELIGIBLE_REASON,
        LOAD_MASTERS.GENDER_BY_GENDER_FOR_CHECKIN,
        LOAD_MASTERS.POS_COUNTRY_ALL,
        LOAD_MASTERS.COUNTRY_COUNTRYI18N_ALL,
      ],
      true
    );
  }

  override destroy(): void {}
}
